import React, { useEffect, useState } from 'react';
import { Select, DatePicker, Card, Row, Col, Input, Button, Divider, Typography, Empty, Upload, message, Checkbox, AutoComplete, Tag } from 'antd';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import { FileText, Eye, Plus, Trash, X, Printer, Save, UploadCloud, Paperclip, Building } from 'lucide-react';
import InvoicePreview from '../components/InvoicePreview';
import AdhocInvoicePreview from '../components/AdhocInvoicePreview';
import HSNSearchDropdown from '../components/HSNSearchDropdown';
import CodeTypeSelect, { codeTypeFromIsServc, isServcFromCodeType } from '../components/CodeTypeSelect';
import { formatIndianCurrency } from '../utils/numberToWords';
import { formatInvoiceNumber } from '../utils/invoiceNumber';
import { getClientCreditNotes, markCreditNotesApplied, fetchInvoiceCategories, fetchDepartments, fetchInvoiceRules, fetchInvoiceTypes, fetchConfig, uploadPurchaseInvoiceFile, fetchVendors } from '../utils/api';
import { calculateEscalatedRent, getEscalationInfo } from '../utils/rentEscalation';
import { UOM_OPTIONS } from '../utils/uom';

const { Title, Text } = Typography;

function Invoice({
  clients,
  selectedClient,
  setSelectedClient,
  invoiceItems,
  setInvoiceItems,
  invoiceNumber,
  setInvoiceNumber,
  invoiceDate,
  setInvoiceDate,
  financialYear,
  setFinancialYear,
  handleItemChange,
  addInvoiceItem,
  removeInvoiceItem,
  generateInvoice,
  showInvoicePreview,
  setShowInvoicePreview,
  calculateTotals,
  numberToWords,
  printRef,
  handlePrint,
  poNumber,
  setPoNumber,
  poDate,
  setPoDate,
  purchaseInvoiceNumber,
  setPurchaseInvoiceNumber,
  purchaseInvoiceFile,
  setPurchaseInvoiceFile,
  isAdhocClientInvoice,
  setIsAdhocClientInvoice,
  vendorName,
  setVendorName,
  vendorId,
  setVendorId,
  vendorInvoiceDate,
  setVendorInvoiceDate,
  vendorPoNumber,
  setVendorPoNumber,
  vendorPoDate,
  setVendorPoDate,
  department,
  setDepartment,
  showShipTo,
  setShowShipTo,
  facilities,
  roundOffEnabled,
  setRoundOffEnabled,
  notes,
  setNotes,
  config
}) {
  const paymentTerms = Array.isArray(config?.paymentTerms) ? config.paymentTerms : [];
  const [selectedPaymentTermId, setSelectedPaymentTermId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [uploadingFile, setUploadingFile] = useState(false);
  const [vendorsList, setVendorsList] = useState([]);
  // Departments state
  const [departments, setDepartments] = useState([]);
  // Invoice category and rules state
  const [invoiceCategories, setInvoiceCategories] = useState([]);
  const [invoiceRules, setInvoiceRules] = useState([]);
  const [invoiceTypes, setInvoiceTypes] = useState([]);
  const [selectedInvoiceCategoryId, setSelectedInvoiceCategoryId] = useState('');
  const [matchedInvoiceRule, setMatchedInvoiceRule] = useState(null);
  const [isRentFormat, setIsRentFormat] = useState(false);
  const [bankAccountFromRule, setBankAccountFromRule] = useState(null);
  const [localConfig, setLocalConfig] = useState(config);
  // Track previous format to handle transitions
  const isRentFormatRef = React.useRef(isRentFormat);

  // Load vendors list
  useEffect(() => {
    fetchVendors({ limit: 100 })
      .then(res => {
        const list = res?.vendors || (Array.isArray(res) ? res : []);
        setVendorsList(list);
      })
      .catch(err => console.error('Failed to load vendors:', err));
  }, []);

  // When Adhoc Client Invoice toggle changes, update format
  useEffect(() => {
    if (isAdhocClientInvoice) {
      setIsRentFormat(false);
    }
  }, [isAdhocClientInvoice]);

  // File upload handler for optional purchase invoice document
  const handleFileUpload = async (file) => {
    if (file.size > 15 * 1024 * 1024) {
      message.error('File size must be less than 15MB');
      return false;
    }
    setUploadingFile(true);
    try {
      const res = await uploadPurchaseInvoiceFile(file);
      setPurchaseInvoiceFile({
        url: res.url,
        originalName: res.originalName || file.name
      });
      message.success('Purchase invoice uploaded successfully');
    } catch (err) {
      console.error('File upload error:', err);
      message.error(err.message || 'Failed to upload purchase invoice');
    } finally {
      setUploadingFile(false);
    }
    return false;
  };

  // Sync config prop with local state
  useEffect(() => {
    if (config && config.bankAccounts && config.bankAccounts.length > 0) {
      setLocalConfig(config);
    }

    // Default to NET 10 if available and not already set
    if (paymentTerms.length > 0 && !selectedPaymentTermId && config) {
      const net10 = paymentTerms.find(t => t.days === 10 || t.label?.toUpperCase() === 'NET 10');
      if (net10) {
        setSelectedPaymentTermId(net10.id);
      }
    }
  }, [config, paymentTerms, selectedPaymentTermId]);

  // Compute due date when invoice date or selected payment term changes
  useEffect(() => {
    try {
      const term = paymentTerms.find(t => String(t.id) === String(selectedPaymentTermId));
      if (!invoiceDate || !term || typeof term.days !== 'number') {
        setDueDate('');
        return;
      }
      const base = new Date(invoiceDate);
      if (isNaN(base.getTime())) {
        setDueDate('');
        return;
      }
      const result = new Date(base);
      result.setDate(result.getDate() + term.days);
      setDueDate(format(result, 'yyyy-MM-dd'));
    } catch {
      setDueDate('');
    }
  }, [invoiceDate, selectedPaymentTermId, paymentTerms]);

  // Load invoice categories, departments, rules, types, and config if needed
  useEffect(() => {
    const loadData = async () => {
      try {
        const [categories, depts, rules, types, configData] = await Promise.all([
          fetchInvoiceCategories(),
          fetchDepartments(),
          fetchInvoiceRules(),
          fetchInvoiceTypes(),
          // Fetch config if it doesn't have bankAccounts
          (!config || !config.bankAccounts || config.bankAccounts.length === 0) ? fetchConfig() : Promise.resolve(null)
        ]);
        setInvoiceCategories(Array.isArray(categories) ? categories : []);
        setDepartments(Array.isArray(depts) ? depts : []);
        setInvoiceRules(Array.isArray(rules) ? rules : []);
        setInvoiceTypes(Array.isArray(types) ? types : []);
        // Update local config if we fetched it
        if (configData && configData.bankAccounts && configData.bankAccounts.length > 0) {
          setLocalConfig(configData);
        }
      } catch (error) {
        console.error('Failed to load invoice data:', error);
      }
    };
    loadData();
  }, [config]);

  // Find matching invoice rule when client and category are selected
  useEffect(() => {
    if (selectedClient && selectedInvoiceCategoryId && invoiceRules.length > 0) {
      const client = clients.find(c => c._id === selectedClient);
      if (client && client.facilityId) {
        // Find rule matching facility and category
        // Rules require facilityId, invoiceCategoryId, and invoiceTypeId
        // Since we might not know invoiceTypeId upfront, find rules matching facility and category
        // If multiple rules exist, prefer ones with invoiceTypeId set
        const matchingRules = invoiceRules.filter(
          rule =>
            String(rule.facilityId) === String(client.facilityId) &&
            String(rule.invoiceCategoryId) === String(selectedInvoiceCategoryId)
        );

        // Prefer rules that have invoiceTypeId set
        let matchingRule = matchingRules.find(rule => rule.invoiceTypeId);

        // If no rule with invoiceTypeId, use first matching rule (backward compatibility)
        if (!matchingRule && matchingRules.length > 0) {
          matchingRule = matchingRules[0];
        }

        if (matchingRules.length > 1 && !matchingRule?.invoiceTypeId) {
          console.warn(`Multiple rules found for facility ${client.facilityId} and category ${selectedInvoiceCategoryId}, using first one`);
        }

        if (matchingRule) {
          setMatchedInvoiceRule(matchingRule);

          // Get invoice type name to determine format
          const invoiceTypeId = matchingRule.invoiceTypeId;
          const invoiceType = invoiceTypes.find(t => String(t.id) === String(invoiceTypeId));

          // Determine format based on invoice type name
          // If invoice type name contains "rent" (case-insensitive), use rent format
          let formatIsRent = false;
          if (invoiceType && invoiceType.name) {
            const typeName = invoiceType.name.toLowerCase();
            formatIsRent = typeName.includes('rent');
          }
          setIsRentFormat(formatIsRent);

          // Get bank account from rule - use localConfig which has bankAccounts
          let bankAccount = null;
          const configToUse = localConfig && localConfig.bankAccounts ? localConfig : config;

          if (!matchingRule.bankAccountId) {
            console.warn('Matching rule found but has no bankAccountId', matchingRule);
          } else if (configToUse.bankAccounts && Array.isArray(configToUse.bankAccounts) && configToUse.bankAccounts.length > 0) {
            // Try multiple matching strategies
            const ruleBankAccountId = matchingRule.bankAccountId;

            // Strategy 1: Exact string match
            bankAccount = configToUse.bankAccounts.find(
              bank => String(bank.id) === String(ruleBankAccountId)
            );

            // Strategy 2: Number match
            if (!bankAccount) {
              bankAccount = configToUse.bankAccounts.find(
                bank => Number(bank.id) === Number(ruleBankAccountId)
              );
            }

            // Strategy 3: Loose match (handle any type conversion)
            if (!bankAccount) {
              bankAccount = configToUse.bankAccounts.find(
                bank => {
                  const bankId = String(bank.id || bank._id || '');
                  const ruleId = String(ruleBankAccountId || '');
                  return bankId === ruleId || Number(bankId) === Number(ruleId);
                }
              );
            }

            if (bankAccount) {
              console.log('Bank account found from rule:', {
                ruleBankAccountId,
                foundBankAccountId: bankAccount.id,
                bankName: bankAccount.bankName
              });
            } else {
              console.warn(`Bank account with ID ${ruleBankAccountId} not found in config. Available bank account IDs:`,
                configToUse.bankAccounts.map(b => b.id));
              // Fallback to first bank account only if no match found
              if (configToUse.bankAccounts.length > 0) {
                console.warn('Using first bank account as fallback');
                bankAccount = configToUse.bankAccounts[0];
              }
            }
          } else {
            console.warn('No bank accounts found in config', {
              localConfigHasBankAccounts: localConfig?.bankAccounts?.length > 0,
              configHasBankAccounts: config?.bankAccounts?.length > 0,
              configToUseHasBankAccounts: configToUse?.bankAccounts?.length > 0
            });
          }
          setBankAccountFromRule(bankAccount || null);
        } else {
          setMatchedInvoiceRule(null);
          setIsRentFormat(false);
          setBankAccountFromRule(null);
        }
      }
    } else {
      setMatchedInvoiceRule(null);
      setIsRentFormat(false);
      setBankAccountFromRule(null);
    }
  }, [selectedClient, selectedInvoiceCategoryId, invoiceRules, invoiceTypes, clients, config.bankAccounts, localConfig]);

  const { subtotal, cgst, sgst, total, roundOff } = calculateTotals();
  const [appliedNoteIds, setAppliedNoteIds] = useState([]);
  const [hasPo, setHasPo] = useState(Boolean(poNumber) || Boolean(poDate));

  useEffect(() => {
    if (poNumber || poDate) {
      setHasPo(true);
    }
  }, [poNumber, poDate]);

  // Get selected client data
  const selectedClientData = clients.find(client => client._id === selectedClient);

  // Handle client selection
  const handleClientChange = (clientId) => {
    setSelectedClient(clientId);
    // Reset category when client changes
    setSelectedInvoiceCategoryId('');

    // Reset items when client changes
    // Default to adhoc structure; if it becomes Rent, the effect will handle it.
    setInvoiceItems([{ description: '', unitPrice: '', qty: '', netAmount: '', tax: '', taxAmount: '', totalAmount: '', hsn: '', IsServc: 'N' }]);
  };

  // Handle invoice category change
  const handleInvoiceCategoryChange = (categoryId) => {
    setSelectedInvoiceCategoryId(categoryId);
  };

  // When client or invoice type/items load, attempt to prefill items from existing credit notes (for rent only)
  useEffect(() => {
    const loadCredits = async () => {
      if (!selectedClient || !isRentFormat) return;
      try {
        const notes = await getClientCreditNotes(selectedClient, 'rent');
        if (notes && notes.length > 0) {
          // Map credit note items to invoice items (positive amounts)
          const creditItems = notes.flatMap(n => {
            const rawNum = n.creditNoteNumber || n.invoiceNumber || '';
            const lastSeg = String(rawNum).split('/').pop();
            const shortNum = lastSeg ? `CN/${lastSeg}` : rawNum;
            let dateStr = '';
            try { dateStr = format(new Date(n.invoiceDate), 'dd-MMM-yy'); } catch { dateStr = ''; }
            const refText = `Reference: Credit Note No. ${shortNum}${dateStr ? ` dated ${dateStr}` : ''} (for return adjustment)`;
            return (n.items || []).map(it => ({
              hsn: it.hsn || '',
              description: refText,
              amount: -Math.abs(Number(it.amount || 0))
            }));
          });
          if (creditItems.length > 0) {
            setAppliedNoteIds(notes.map(n => n._id));
            // Merge with current invoiceItems by appending after first row
            const merged = [...invoiceItems];
            const insertAt = Math.min(1, merged.length);
            merged.splice(insertAt, 0, ...creditItems);
            setInvoiceItems(merged);
          }
        }
      } catch (e) {
        // Ignore failures; don't block invoice creation
      }
    };
    loadCredits();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient, isRentFormat]);

  // Auto-select Financial Year based on invoiceDate and disable editing
  useEffect(() => {
    try {
      const d = new Date(invoiceDate);
      if (!isNaN(d)) {
        const year = d.getFullYear();
        const month = d.getMonth() + 1; // 1-12
        const startYear = month >= 4 ? year : year - 1;
        const endYear = startYear + 1;
        const fy = `${startYear}-${endYear}`;
        if (fy !== financialYear) setFinancialYear(fy);
      }
    } catch { }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceDate]);

  // Master Effect: Handle Format Transitions and Rent Calculation
  useEffect(() => {
    const prevIsRent = isRentFormatRef.current;

    // Get the selected category name to check if it's "Rent - Office"
    const selectedCategory = invoiceCategories.find(cat => cat.id === selectedInvoiceCategoryId);
    const isRentOfficeCategory = selectedCategory?.name?.toLowerCase().trim() === 'rent - office';

    // CASE 1: Transitioning TO Rent (Adhoc -> Rent) OR Rent -> Rent (Update)
    // Only populate line items if category is "Rent - Office"
    if (isRentFormat && isRentOfficeCategory) {
      if (selectedClient && invoiceDate) {
        const client = clients.find(c => c._id === selectedClient);
        if (client && client.rentAmount) {
          // Calculate escalated rent amount
          const escalatedRent = calculateEscalatedRent(
            client.rentAmount,
            client.escalationPercentage,
            client.rentStartDate,
            invoiceDate
          );

          // Get escalation info for display
          const escalationInfo = getEscalationInfo(
            client.rentAmount,
            client.escalationPercentage,
            client.rentStartDate,
            invoiceDate
          );

          // Get facility name
          const facilityName = facilities.find(fac => String(fac.id) === String(client.facilityId))?.name || '';
          // Format month and year from invoiceDate
          let monthYear = '';
          try {
            monthYear = format(new Date(invoiceDate), "MMM''yy"); // e.g. May'25
          } catch {
            monthYear = '';
          }

          // Build description with escalation info if applicable
          let description = `Rent for the month of ${monthYear} - Workstation${facilityName ? ' - ' + facilityName : ''}`;
          if (escalationInfo.hasEscalation) {
            description += ` (Escalated by ${escalationInfo.escalationPercentage}% for ${escalationInfo.yearsElapsed} year${escalationInfo.yearsElapsed > 1 ? 's' : ''})`;
          }

          // Define the Rent Item Request
          const rentItem = {
            description,
            amount: escalatedRent,
            hsn: '997212',
            rate: '', // Rent format usually doesn't show rate? Or is rate == amount?
            // Existing logic didn't set rate, assumed amount is key.
            // We ensure Adhoc keys are undefined/cleared implicitly by not including them or explicitly clearing if strictly required.
          };

          // If switching format OR current items look incompatible, FORCE RESET to single rent item
          if (!prevIsRent) {
            setInvoiceItems([rentItem]);
          } else {
            // If already Rent, just update the first item in place (Soft Update) to preserve other potential manual edits?
            // Actually, for Rent, typically it's just one line. But if user added lines? 
            // Existing logic was `updatedItems[0] = { ... }`.
            setInvoiceItems(prevItems => {
              if (prevItems.length === 0) return [rentItem];
              const newItems = [...prevItems];
              newItems[0] = { ...newItems[0], ...rentItem };
              return newItems;
            });
          }
        }
      }
    }
    // CASE 2: Transitioning TO Adhoc (Rent -> Adhoc)
    else if (!isRentFormat && prevIsRent) {
      // Reset to Adhoc Default
      setInvoiceItems([{ description: '', unitPrice: '', qty: '', netAmount: '', tax: '', taxAmount: '', totalAmount: '', hsn: '', IsServc: 'N' }]);
    }

    // Capture current state for next run
    isRentFormatRef.current = isRentFormat;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceDate, selectedClient, isRentFormat, clients, invoiceCategories, selectedInvoiceCategoryId]);

  // Helpers for adhoc invoice
  const toNumber = (value, fallback = 0) => {
    const cleaned = String(value ?? '').replace(/[^0-9.-]/g, '');
    const n = parseFloat(cleaned);
    return Number.isFinite(n) ? n : fallback;
  };

  // Calculate item totals for adhoc invoice
  const calculateAdhocItemTotals = (item) => {
    const qty = Math.max(0, toNumber(item.qty, 1));
    const unitPriceRaw = toNumber(item.unitPrice);
    const unitPrice = unitPriceRaw > 0 ? unitPriceRaw : (qty > 0 ? toNumber(item.netAmount) / qty : 0);
    const taxRate = Math.max(0, toNumber(item.tax ?? item.taxPercent, 0));

    const netAmount = unitPrice * qty;
    const taxAmount = netAmount * (taxRate / 100);
    const totalAmount = netAmount + taxAmount;

    return { netAmount, taxAmount, totalAmount };
  };

  // Handle item change for adhoc invoice
  const handleAdhocItemChange = (index, field, value) => {
    const newItems = [...invoiceItems];
    // Sanitize numeric fields to avoid values like '300c' causing NaN
    let nextValue = value;
    if (['unitPrice', 'qty', 'tax'].includes(field)) {
      // Just clean invalid chars but keep as string to allow typing "0.5" etc.
      // Don't force Number() immediately as it breaks "1." input (becomes 1)
      const cleaned = String(value).replace(/[^0-9.-]/g, '');
      nextValue = cleaned;
    }
    newItems[index][field] = nextValue;

    // Recalculate totals when unit price, qty, or tax changes
    if (['unitPrice', 'qty', 'tax'].includes(field)) {
      const { netAmount, taxAmount, totalAmount } = calculateAdhocItemTotals(newItems[index]);
      newItems[index].netAmount = netAmount;
      newItems[index].taxAmount = taxAmount;
      newItems[index].totalAmount = totalAmount;
    }

    setInvoiceItems(newItems);
  };

  // Add new adhoc invoice item
  const addAdhocInvoiceItem = () => {
    setInvoiceItems([...invoiceItems, {
      description: '',
      unitPrice: '',
      qty: '',
      uom: 'PCS',
      netAmount: '',
      tax: '',
      taxAmount: '',
      totalAmount: '',
      hsn: '',
      IsServc: 'N'
    }]);
  };

  // Calculate adhoc invoice totals
  const calculateAdhocTotals = () => {
    // Recompute from current inputs instead of trusting stored fields
    const totals = invoiceItems.reduce((acc, item) => {
      const { netAmount, taxAmount } = calculateAdhocItemTotals(item);
      acc.net += netAmount;
      acc.tax += taxAmount;
      return acc;
    }, { net: 0, tax: 0 });
    const totalNetAmount = Math.round(totals.net * 100) / 100;
    const totalTaxAmount = Math.round(totals.tax * 100) / 100;
    const grossAmount = totalNetAmount + totalTaxAmount;
    const roundedTotal = Math.round(grossAmount);
    const roundOff = roundOffEnabled ? Math.round((roundedTotal - grossAmount) * 100) / 100 : 0;
    const totalAmount = roundOffEnabled ? roundedTotal : Math.round(grossAmount * 100) / 100;
    return { totalNetAmount, totalTaxAmount, totalAmount, roundOff };
  };

  const adhocTotals = !isRentFormat ? calculateAdhocTotals() : null;
  const { totalNetAmount, totalTaxAmount, totalAmount: adhocTotal, roundOff: adhocRoundOff } = adhocTotals || {};

  // HSN (goods) items require a UOM; SAC (service) items make it optional
  const validateCodeTypeUom = () => {
    const idx = invoiceItems.findIndex(it => (it.IsServc || 'N') !== 'Y' && !it.uom);
    if (idx !== -1) {
      alert(`Row ${idx + 1}: UOM is required for items with HSN Code.`);
      return false;
    }
    return true;
  };

  // After invoice generation, mark notes applied (for rent only)
  const wrappedGenerate = async () => {
    // Rent format has no UOM / code-type columns, so skip that validation
    // (adhoc format validates in wrappedGenerateAdhoc)
    // Get bank account to use (from rule or fallback to first in config)
    const configToUse = localConfig && localConfig.bankAccounts ? localConfig : config;
    const bankAccountToUse = bankAccountFromRule || (configToUse.bankAccounts && configToUse.bankAccounts.length > 0 ? configToUse.bankAccounts[0] : null);

    const term = paymentTerms.find(t => String(t.id) === String(selectedPaymentTermId));
    if (!term) {
      alert('Please select a payment term (due date) before generating the invoice.');
      return;
    }
    const paymentTermPayload = {
      label: term.label || '',
      days: typeof term.days === 'number' ? term.days : 0,
      dueDate: dueDate || null
    };
    await generateInvoice(!isRentFormat, bankAccountToUse, selectedInvoiceCategoryId, paymentTermPayload); // Pass isAdhocFormat, bankAccount, invoiceCategoryId, paymentTerm
    if (appliedNoteIds.length > 0 && isRentFormat) {
      try { await markCreditNotesApplied(appliedNoteIds); } catch { }
    }
  };

  // Wrapper for adhoc invoice generation
  const wrappedGenerateAdhoc = async () => {
    if (!validateCodeTypeUom()) return;
    // Get bank account to use (from rule or fallback to first in config)
    const configToUse = localConfig && localConfig.bankAccounts ? localConfig : config;
    const bankAccountToUse = bankAccountFromRule || (configToUse.bankAccounts && configToUse.bankAccounts.length > 0 ? configToUse.bankAccounts[0] : null);

    const term = paymentTerms.find(t => String(t.id) === String(selectedPaymentTermId));
    if (!term) {
      alert('Please select a payment term (due date) before generating the invoice.');
      return;
    }
    const paymentTermPayload = {
      label: term.label || '',
      days: typeof term.days === 'number' ? term.days : 0,
      dueDate: dueDate || null
    };
    await generateInvoice(true, bankAccountToUse, selectedInvoiceCategoryId, paymentTermPayload); // Pass isAdhocFormat, bankAccount, invoiceCategoryId, paymentTerm
  };

  // Create client object with bank account from rule for preview
  const getClientForPreview = () => {
    if (!selectedClientData) return null;
    return {
      ...selectedClientData,
      bankAccountId: bankAccountFromRule ? bankAccountFromRule.id : selectedClientData.bankAccountId
    };
  };

  // Create config object with bank account from rule for preview
  const getConfigForPreview = () => {
    const configToUse = localConfig && localConfig.bankAccounts ? localConfig : config;
    return {
      ...configToUse,
      bankAccounts: bankAccountFromRule ? [bankAccountFromRule] : (configToUse.bankAccounts || [])
    };
  };

  return (
    <div className="page-container">
      {/* Header Section */}
      <Row justify="space-between" align="middle" style={{ marginBottom: 24 }}>
        <Col>
          <Title level={2} style={{ margin: 0 }}>Create New Invoice</Title>
          <Text type="secondary">Drafting invoice for standard retail transaction</Text>
        </Col>

      </Row>

      {/* General Information Card */}
      <Card
        title={<><FileText size={18} style={{ marginRight: 8, color: '#1890ff' }} />General Information</>}
        style={{ marginBottom: 24, borderRadius: 8, boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)' }}
        bodyStyle={{ padding: 24 }}
      >
        {/* Adhoc Client Invoice Checkbox Header */}
        <div style={{
          marginBottom: 20,
          padding: '14px 18px',
          background: isAdhocClientInvoice ? '#eff6ff' : '#f8fafc',
          border: isAdhocClientInvoice ? '1px solid #bfdbfe' : '1px solid #e2e8f0',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 12
        }}>
          <div>
            <Checkbox
              checked={isAdhocClientInvoice}
              onChange={(e) => {
                const checked = e.target.checked;
                setIsAdhocClientInvoice(checked);
                if (checked) {
                  setIsRentFormat(false);
                } else {
                  setIsRentFormat(true);
                }
              }}
              style={{ fontWeight: 600, fontSize: '15px' }}
            >
              Adhoc Client Invoice
            </Checkbox>
            <div style={{ fontSize: '12px', color: '#64748b', marginLeft: 24, marginTop: 2 }}>
              Check this to record vendor/purchase invoice details, PO info, and department for adhoc reselling.
            </div>
          </div>
          {isAdhocClientInvoice && (
            <Tag color="blue" style={{ margin: 0, fontWeight: 500, padding: '4px 10px', fontSize: '12px' }}>
              Adhoc Line Items Active
            </Tag>
          )}
        </div>

        {/* Row 1: Core Details (Client, Category, Invoice Number) */}
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Client Selection</Text></div>
              <Select
                style={{ width: '100%' }}
                value={selectedClient || undefined}
                onChange={(v) => handleClientChange(v)}
                placeholder="Select Client"
                options={clients.map(c => ({ label: c.name, value: c._id }))}
                showSearch
                optionFilterProp="label"
                allowClear
              />
              {selectedClientData && (
                <div style={{ marginTop: 10, background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: '8px', padding: '10px 12px' }}>
                  <div style={{ fontWeight: '700', fontSize: '13px', marginBottom: '2px' }}>{selectedClientData.name}</div>
                  <div style={{ fontSize: '12px', color: '#64748b' }}>GSTIN: {selectedClientData.gstin || '-'}</div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{selectedClientData.billToAddress || 'No address available'}</div>
                </div>
              )}
            </div>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Category</Text></div>
              <Select
                style={{ width: '100%' }}
                value={selectedInvoiceCategoryId || undefined}
                onChange={handleInvoiceCategoryChange}
                placeholder="Select Category"
                options={invoiceCategories.map(cat => ({ label: cat.name, value: cat.id }))}
                showSearch
                optionFilterProp="label"
                allowClear
                disabled={!selectedClient}
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Invoice Number</Text></div>
              <Input
                style={{ width: '100%', color: '#000', cursor: 'default' }}
                value={formatInvoiceNumber(invoiceNumber, financialYear, config)}
                placeholder="Invoice Number"
                disabled
                className="disabled-input-text"
              />
            </div>
          </Col>
        </Row>

        {/* Row 2: Dates, Terms & Shipping */}
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Invoice Date</Text></div>
              <DatePicker
                style={{ width: '100%' }}
                value={invoiceDate ? dayjs(invoiceDate) : null}
                onChange={(d) => setInvoiceDate(d ? d.format('YYYY-MM-DD') : '')}
                format="DD/MM/YYYY"
                placeholder="Select Date"
                allowClear
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Payment Terms</Text></div>
              <Select
                style={{ width: '100%' }}
                value={selectedPaymentTermId || undefined}
                onChange={(v) => setSelectedPaymentTermId(v)}
                placeholder="Select Terms"
                options={paymentTerms.map(t => ({
                  label: t.label || `NET ${t.days} `,
                  value: t.id
                }))}
                allowClear
              />
            </div>
          </Col>

          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Shipping Address</Text></div>
              <Select
                style={{ width: '100%' }}
                value={showShipTo}
                onChange={(v) => setShowShipTo(v)}
                options={[{ label: 'No', value: 'no' }, { label: 'Yes', value: 'yes' }]}
              />
            </div>
          </Col>
        </Row>

        {/* Row 3: Client PO */}
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12} md={8}>
            <div style={{ marginBottom: 20 }}>
              <div style={{ marginBottom: 8 }}><Text strong>Have PO Number?</Text></div>
              <Select
                style={{ width: '100%' }}
                value={hasPo ? 'yes' : 'no'}
                onChange={(v) => {
                  const has = v === 'yes';
                  setHasPo(has);
                  if (!has) {
                    setPoNumber('');
                    setPoDate('');
                  }
                }}
                options={[
                  { label: 'No', value: 'no' },
                  { label: 'Yes', value: 'yes' }
                ]}
              />
            </div>
          </Col>

          {hasPo && (
            <>
              <Col xs={24} sm={12} md={8}>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ marginBottom: 8 }}><Text strong>PO Number</Text></div>
                  <Input
                    placeholder="Enter PO Number"
                    value={poNumber || ''}
                    onChange={e => setPoNumber(e.target.value)}
                    allowClear
                  />
                </div>
              </Col>

              <Col xs={24} sm={12} md={8}>
                <div style={{ marginBottom: 20 }}>
                  <div style={{ marginBottom: 8 }}><Text strong>PO Date</Text></div>
                  <DatePicker
                    style={{ width: '100%' }}
                    value={poDate ? dayjs(poDate) : null}
                    onChange={(d) => setPoDate(d ? d.format('YYYY-MM-DD') : '')}
                    format="DD/MM/YYYY"
                    placeholder="Select PO Date"
                    allowClear
                  />
                </div>
              </Col>
            </>
          )}
        </Row>

        {/* Adhoc Procurement & Vendor Details Section - Revealed when checkbox is checked */}
        {isAdhocClientInvoice && (
          <div style={{
            marginTop: 8,
            padding: '20px',
            background: '#f8fafc',
            borderRadius: 8,
            border: '1px solid #e2e8f0',
            marginBottom: 8
          }}>
            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: 16, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 6 }}>
              <Building size={16} color="#2563eb" />
              <span>Vendor, PO & Department Details (Internal Tracking)</span>
            </div>
            <Row gutter={[24, 16]}>
              <Col xs={24} sm={12} md={8}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 6 }}><Text strong>Department</Text></div>
                  <Select
                    style={{ width: '100%' }}
                    value={department || undefined}
                    onChange={(val) => setDepartment(val || '')}
                    placeholder="Select Department"
                    options={departments.map(dept => ({ label: dept.name, value: dept.name }))}
                    showSearch
                    optionFilterProp="label"
                    allowClear
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 6 }}><Text strong>Vendor Name</Text></div>
                  <AutoComplete
                    style={{ width: '100%' }}
                    value={vendorName}
                    options={vendorsList.map(v => ({ value: v.name }))}
                    onChange={(val) => {
                      setVendorName(val || '');
                      const found = vendorsList.find(v => (v.name || '').toLowerCase() === (val || '').toLowerCase());
                      setVendorId(found ? found._id : '');
                    }}
                    placeholder="Select or type vendor name"
                    filterOption={(inputValue, option) =>
                      (option?.value ?? '').toUpperCase().indexOf((inputValue || '').toUpperCase()) !== -1
                    }
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 6 }}><Text strong>Vendor Invoice No.</Text></div>
                  <Input
                    placeholder="Enter vendor invoice number"
                    value={purchaseInvoiceNumber || ''}
                    onChange={e => setPurchaseInvoiceNumber(e.target.value)}
                    allowClear
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 6 }}><Text strong>Vendor Invoice Date</Text></div>
                  <DatePicker
                    style={{ width: '100%' }}
                    value={vendorInvoiceDate ? dayjs(vendorInvoiceDate) : null}
                    onChange={(d) => setVendorInvoiceDate(d ? d.format('YYYY-MM-DD') : '')}
                    format="DD/MM/YYYY"
                    placeholder="Select Vendor Invoice Date"
                    allowClear
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 6 }}><Text strong>Vendor PO Number</Text></div>
                  <Input
                    placeholder="Enter Vendor PO Number"
                    value={vendorPoNumber || ''}
                    onChange={e => setVendorPoNumber(e.target.value)}
                    allowClear
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 6 }}><Text strong>Vendor PO Date</Text></div>
                  <DatePicker
                    style={{ width: '100%' }}
                    value={vendorPoDate ? dayjs(vendorPoDate) : null}
                    onChange={(d) => setVendorPoDate(d ? d.format('YYYY-MM-DD') : '')}
                    format="DD/MM/YYYY"
                    placeholder="Select Vendor PO Date"
                    allowClear
                  />
                </div>
              </Col>
              <Col xs={24} sm={12} md={8}>
                <div style={{ marginBottom: 16 }}>
                  <div style={{ marginBottom: 6 }}><Text strong>Attach Vendor Invoice (Optional)</Text></div>
                  {purchaseInvoiceFile ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '7px 12px',
                      background: '#f0fdf4',
                      border: '1px solid #bbf7d0',
                      borderRadius: 6
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, overflow: 'hidden' }}>
                        <Paperclip size={16} color="#16a34a" />
                        <span style={{ fontSize: '13px', fontWeight: 500, color: '#15803d', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 180 }}>
                          {purchaseInvoiceFile.originalName || 'Attached Invoice'}
                        </span>
                      </div>
                      <Button
                        type="text"
                        size="small"
                        danger
                        icon={<Trash size={14} />}
                        onClick={() => setPurchaseInvoiceFile(null)}
                        title="Remove file"
                      />
                    </div>
                  ) : (
                    <Upload
                      beforeUpload={handleFileUpload}
                      showUploadList={false}
                      accept=".pdf,image/*"
                      multiple={false}
                    >
                      <Button icon={<UploadCloud size={16} />} loading={uploadingFile} style={{ width: '100%' }}>
                        {uploadingFile ? 'Uploading...' : 'Upload PDF / Image'}
                      </Button>
                    </Upload>
                  )}
                </div>
              </Col>
            </Row>
          </div>
        )}
      </Card>

      {/* Line Items Card */}
      <Card
        title={<><FileText size={18} style={{ marginRight: 8, color: '#1890ff' }} />Line Items</>}
        style={{ marginBottom: 24, borderRadius: 8, boxShadow: '0 1px 2px 0 rgba(0,0,0,0.03)' }}
        bodyStyle={{ padding: 0 }}
      >
        {!selectedInvoiceCategoryId ? (
          <div style={{ padding: '60px 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <Empty
              description={
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  <Text strong>No Line Items</Text>
                  <Text type="secondary">Please select an invoice category to start adding items</Text>
                </div>
              }
            />
          </div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead style={{ background: '#fafafa' }}>
                  <tr>
                    <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 50, textAlign: 'center', color: '#64748b' }}>Sr.</th>
                    <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 120, textAlign: 'left', color: '#64748b' }}>HSN/SAC</th>
                    <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', textAlign: 'left', color: '#64748b' }}>Description</th>
                    {isRentFormat ? (
                      <>
                        <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 130, textAlign: 'right', color: '#64748b' }}>Amount</th>
                        <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 80, textAlign: 'center', color: '#64748b' }}>CGST %</th>
                        <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 120, textAlign: 'right', color: '#64748b' }}>CGST Amount</th>
                        <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 80, textAlign: 'center', color: '#64748b' }}>SGST %</th>
                        <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 120, textAlign: 'right', color: '#64748b' }}>SGST Amount</th>
                      </>
                    ) : (
                      <>
                        <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 130, textAlign: 'right', color: '#64748b' }}>Unit Price</th>
                        <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 80, textAlign: 'center', color: '#64748b' }}>Qty</th>
                        <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 110, textAlign: 'left', color: '#64748b' }}>UOM</th>
                        <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 130, textAlign: 'right', color: '#64748b' }}>Net Amt</th>
                        <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 100, textAlign: 'center', color: '#64748b' }}>Tax %</th>
                        <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 120, textAlign: 'right', color: '#64748b' }}>Tax Amt</th>
                        <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 110, textAlign: 'center', color: '#64748b' }}>Code Type</th>
                      </>
                    )}
                    <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 140, textAlign: 'right', color: '#64748b' }}>Total</th>
                    <th style={{ padding: '16px 12px', borderBottom: '1px solid #f0f0f0', width: 50 }}></th>
                  </tr>
                </thead>
                <tbody>
                  {invoiceItems.map((item, index) => {
                    // Rent Mode Calculations
                    const cgstRate = config && config.cgstRate !== undefined ? config.cgstRate : 0;
                    const sgstRate = config && config.sgstRate !== undefined ? config.sgstRate : 0;
                    const baseRentAmount = Number(item.amount || 0);
                    const cgstAmount = baseRentAmount * (cgstRate / 100);
                    const sgstAmount = baseRentAmount * (sgstRate / 100);

                    // Adhoc Mode Calculations
                    const taxPercent = item.tax || 0;
                    const taxAmount = item.taxAmount || 0;

                    const totalRowAmount = isRentFormat
                      ? (baseRentAmount + cgstAmount + sgstAmount)
                      : (Number(item.netAmount || 0) + taxAmount);

                    return (
                      <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                        <td style={{ padding: '12px', textAlign: 'center' }}>{index + 1}</td>
                        <td style={{ padding: '8px' }}>
                          <Input
                            bordered={false}
                            placeholder="HSN"
                            value={item.hsn || ''}
                            onChange={(e) => isRentFormat ? handleItemChange(index, 'hsn', e.target.value) : handleAdhocItemChange(index, 'hsn', e.target.value)}
                            style={{ padding: '4px 8px' }}
                          />
                        </td>
                        <td style={{ padding: '8px' }}>
                          <Input
                            bordered={false}
                            placeholder="Item Description"
                            value={item.description || ''}
                            onChange={(e) => isRentFormat ? handleItemChange(index, 'description', e.target.value) : handleAdhocItemChange(index, 'description', e.target.value)}
                            style={{ fontWeight: 500 }}
                          />
                        </td>

                        {isRentFormat ? (
                          // RENT COLUMNS
                          <>
                            <td style={{ padding: '8px' }}>
                              <Input
                                bordered={false}
                                placeholder="0.00"
                                value={item.amount}
                                onChange={(e) => handleItemChange(index, 'amount', e.target.value)}
                                style={{ textAlign: 'right' }}
                              />
                            </td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>{cgstRate}%</td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>{formatIndianCurrency(cgstAmount)}</td>
                            <td style={{ padding: '12px', textAlign: 'center' }}>{sgstRate}%</td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>{formatIndianCurrency(sgstAmount)}</td>
                          </>
                        ) : (
                          // ADHOC COLUMNS
                          <>
                            <td style={{ padding: '8px' }}>
                              <Input
                                bordered={false}
                                placeholder="0.00"
                                value={item.unitPrice}
                                onChange={(e) => handleAdhocItemChange(index, 'unitPrice', e.target.value)}
                                style={{ textAlign: 'right' }}
                              />
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <Input
                                bordered={false}
                                placeholder="1"
                                value={item.qty}
                                onChange={(e) => handleAdhocItemChange(index, 'qty', e.target.value)}
                                style={{ textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ padding: '8px' }}>
                              <Select
                                variant="borderless"
                                style={{ width: '100%' }}
                                value={item.uom || undefined}
                                onChange={(v) => handleAdhocItemChange(index, 'uom', v)}
                                options={UOM_OPTIONS}
                                showSearch
                                optionFilterProp="label"
                                allowClear
                              />
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>
                              {formatIndianCurrency(Number(item.netAmount || 0))}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <Select
                                variant="borderless"
                                style={{ width: '100%' }}
                                value={item.tax ? String(item.tax) : '0'}
                                onChange={(v) => handleAdhocItemChange(index, 'tax', v)}
                                options={[
                                  { label: '0%', value: '0' },
                                  { label: '5%', value: '5' },
                                  { label: '12%', value: '12' },
                                  { label: '18%', value: '18' },
                                  { label: '28%', value: '28' }
                                ]}
                              />
                            </td>
                            <td style={{ padding: '12px', textAlign: 'right' }}>
                              {formatIndianCurrency(taxAmount)}
                            </td>
                            <td style={{ padding: '8px', textAlign: 'center' }}>
                              <CodeTypeSelect
                                size="small"
                                value={codeTypeFromIsServc(item.IsServc)}
                                onChange={(v) => handleAdhocItemChange(index, 'IsServc', isServcFromCodeType(v))}
                              />
                            </td>
                          </>
                        )}

                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: 600 }}>
                          {formatIndianCurrency(totalRowAmount)}
                        </td>
                        <td style={{ textAlign: 'center' }}>
                          {invoiceItems.length > 1 && (
                            <Button
                              type="text"
                              danger
                              icon={<Trash size={16} />}
                              onClick={() => removeInvoiceItem(index)}
                            />
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div style={{ padding: '16px' }}>
              <Button type="dashed" block onClick={isRentFormat ? addInvoiceItem : addAdhocInvoiceItem} icon={<Plus size={16} />}>
                Add Line Item
              </Button>
            </div>

            <Divider style={{ margin: 0 }} />

            {/* Footer Grid */}
            <Row style={{ padding: 24 }} gutter={[48, 24]}>
              <Col xs={24} sm={24} md={14} lg={14}>
                <div style={{ marginBottom: 8 }}><Text strong>Notes & Terms</Text></div>
                <Input.TextArea
                  rows={4}
                  placeholder="Any specific instructions or terms..."
                  style={{ borderRadius: 6 }}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Col>
              <Col xs={24} sm={24} md={10} lg={10}>
                {/* Totals Panel */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <Row justify="space-between">
                    <Text type="secondary">Subtotal</Text>
                    <Text strong>{formatIndianCurrency(isRentFormat ? subtotal : (totalNetAmount || 0))}</Text>
                  </Row>

                  {isRentFormat ? (
                    <>
                      <Row justify="space-between">
                        <Text type="secondary">CGST ({config?.cgstRate || 0}%)</Text>
                        <Text strong>{formatIndianCurrency(cgst)}</Text>
                      </Row>
                      <Row justify="space-between">
                        <Text type="secondary">SGST ({config?.sgstRate || 0}%)</Text>
                        <Text strong>{formatIndianCurrency(sgst)}</Text>
                      </Row>
                    </>
                  ) : (
                    <Row justify="space-between">
                      <Text type="secondary">Tax Amount</Text>
                      <Text strong>{formatIndianCurrency(totalTaxAmount || 0)}</Text>
                    </Row>
                  )}

                  <Row justify="space-between">
                    <Text type="secondary">Round Off</Text>
                    <Text strong>{formatIndianCurrency(isRentFormat ? roundOff : (adhocRoundOff || 0))}</Text>
                  </Row>

                  <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16, marginTop: 8 }}>
                    <Row justify="space-between" align="middle">
                      <Text strong style={{ fontSize: 18 }}>Grand Total</Text>
                      <Text strong style={{ fontSize: 24, color: '#1890ff' }}>
                        {formatIndianCurrency(isRentFormat ? total : (adhocTotal || 0))}
                      </Text>
                    </Row>
                  </div>

                  <div style={{ display: 'flex', gap: 12, marginTop: 24 }}>
                    <Button
                      icon={<Save size={16} />}
                      hidden
                      onClick={() => {/* TODO: Save Draft Logic */ }}
                      style={{ flex: 1 }}
                    >
                      Save as Draft
                    </Button>
                    <Button
                      type="primary"
                      onClick={isRentFormat ? wrappedGenerate : wrappedGenerateAdhoc}
                      style={{ flex: 1 }}
                    >
                      Save Invoice
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </>
        )}
      </Card>

      {/* Keep the Preview Modal */}
      {showInvoicePreview && selectedClient && (
        <div className="modal-overlay" style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center'
        }}

        >
          <div className="modal-content" style={{
            background: '#fff', borderRadius: '10px', padding: '0', width: '95%', maxWidth: '900px', position: 'relative', boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
            maxHeight: '90vh', overflowY: 'auto', margin: '0 16px'
          }}
            onClick={e => e.stopPropagation()}
          >
            <div className="no-print" style={{ position: 'sticky', top: 0, left: 0, right: 0, background: '#fff', zIndex: 5, padding: '10px 16px', borderBottom: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTopLeftRadius: '10px', borderTopRightRadius: '10px' }}>
              <div style={{ fontWeight: 700, fontSize: 16 }}>
                {formatInvoiceNumber(invoiceNumber, financialYear, config)}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <button className="btn btn-primary" onClick={handlePrint}>
                  <Printer style={{ marginRight: 6 }} />Print Invoice
                </button>
                <button
                  className="btn"
                  style={{ background: 'none', border: 'none', fontSize: '22px', color: '#888', cursor: 'pointer', padding: '6px 8px' }}
                  onClick={() => setShowInvoicePreview(false)}
                  aria-label="Close"
                >
                  <X />
                </button>
              </div>
            </div>
            {isRentFormat ? (
              <InvoicePreview
                client={getClientForPreview()}
                invoiceNumber={invoiceNumber}
                invoiceDate={invoiceDate}
                placeOfSupply={config.defaultPlaceOfSupply || 'Karnataka'}
                items={invoiceItems}
                subtotal={subtotal}
                cgst={cgst}
                sgst={sgst}
                roundOff={roundOff}
                total={total}
                amountInWords={numberToWords(total)}
                printRef={printRef}
                handlePrint={handlePrint}
                config={getConfigForPreview()}
                poNumber={poNumber}
                poDate={poDate}
                showShipTo={showShipTo}
                financialYear={financialYear}
                hideQuoteNumber
                purchaseInvoiceNumber={purchaseInvoiceNumber}
                paymentTerms={{ label: paymentTerms.find(t => t.id === selectedPaymentTermId)?.label, dueDate }}
              />
            ) : (
              <AdhocInvoicePreview
                client={getClientForPreview()}
                invoiceNumber={invoiceNumber}
                invoiceDate={invoiceDate}
                placeOfSupply={config.defaultPlaceOfSupply || 'Karnataka'}
                items={invoiceItems}
                subtotal={totalNetAmount || 0}
                cgst={(totalTaxAmount || 0) / 2}
                sgst={(totalTaxAmount || 0) / 2}
                roundOff={adhocRoundOff || 0}
                total={adhocTotal || 0}
                amountInWords={numberToWords(adhocTotal || 0)}
                printRef={printRef}
                handlePrint={handlePrint}
                config={getConfigForPreview()}
                poNumber={poNumber}
                poDate={poDate}
                purchaseInvoiceNumber={purchaseInvoiceNumber}
                showShipTo={showShipTo}
                financialYear={financialYear}
                hideInlinePrintButton
                paymentTerms={{ label: paymentTerms.find(t => t.id === selectedPaymentTermId)?.label, dueDate }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default Invoice;

